import { useCallback, useEffect, useRef, useState } from "react";

import * as Sentry from "@sentry/react";
import Vapi from "@vapi-ai/web";

import { useNoSleep } from "@/components/pieces/useNoSleep";
import { getUser } from "@/components/session/JwtTokenManager";

import { RoleplaySessionResetConfirm } from "./RoleplaySessionResetConfirm";

const CONNECTION_TIMEOUT = 30000; // 30 seconds
const MAX_RETRY_SAVING_COUNT = 2;

const vapi = new Vapi(VAPI_PUBLIC_KEY);

export enum CallStatus {
  IDLE,
  STARTING,
  CONNECTING,
  CONNECTED,
  RESTARTING,
  ENDING,
  ERROR,
  SAVE_FAILED,
}

export interface CallState {
  status: CallStatus;
  countDown: number;
  error: string | null;
}

// Types for render props
export interface LiveCallRenderProps {
  callState: CallState;
  callDuration: number;
  onStartCall: () => void;
  onCancelCall: () => void;
  onEndCall: () => void;
  onRestartCall: () => void;
  onRetrySave: () => void;
  onOpenResetConfirm: () => void;
  introductionAudioUrl?: string;
  disabled?: boolean;
  sendBackgroundMessage: (message: string) => void;
}

interface LiveCallManagerProps {
  assistantId: string;
  handleSaveCall: (callId?: string) => void;
  saveError?: string;
  onVolumeChange?: (level: number) => void;
  onCallStarted?: () => void;
  onCallRestarted?: () => void;
  introductionAudioUrl?: string;
  disabled?: boolean;
  skipCountdown?: boolean;
  variableValues?: {
    [key: string]: string;
  };
  children: (props: LiveCallRenderProps) => React.ReactNode;
  isConnected?: boolean;
}

// lazy requires a default export
export default function LiveCallManager({
  assistantId,
  handleSaveCall,
  saveError,
  onVolumeChange,
  onCallStarted,
  onCallRestarted,
  variableValues,
  children,
  introductionAudioUrl,
  disabled,
  skipCountdown = false,
  isConnected,
}: LiveCallManagerProps) {
  const [isOpenResetConfirmDialog, setIsOpenResetConfirmDialog] =
    useState(false);

  const [retrySavingCount, setRetrySavingCount] = useState(0);
  const callIdRef = useRef<string | null>(null);
  const endingIntentRef = useRef<
    "save" | "cancel" | "restart" | "error" | null
  >(null);
  const [callDuration, setCallDuration] = useState(0);
  const { enable, disable } = useNoSleep();
  const [callState, setCallState] = useState<CallState>({
    status: CallStatus.IDLE,
    countDown: 3,
    error: null,
  });

  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const user = getUser();
  const startCountdown = useCallback(() => {
    const timer = setInterval(() => {
      setCallState((prev) => {
        if (prev.countDown === 1) {
          clearInterval(timer);
          return { ...prev, status: CallStatus.CONNECTING, countDown: 3 };
        }
        return { ...prev, countDown: prev.countDown - 1 };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (
      callState.status === CallStatus.STARTING ||
      callState.status === CallStatus.RESTARTING
    ) {
      return startCountdown();
    }
  }, [callState.status, startCountdown]);

  async function startCall() {
    try {
      const call = await vapi.start(assistantId, {
        variableValues: {
          name: user?.name,
          ...variableValues,
        },
      });
      callIdRef.current = call?.id ?? null;
      Sentry.addBreadcrumb({
        category: "call",
        message: "Call started",
        level: "info",
      });
    } catch (error) {
      Sentry.captureException(error);
      setCallState((prev) => ({
        ...prev,
        status: CallStatus.ERROR,
        error: "Failed to start the call. Please try again.",
      }));
    }
  }

  useEffect(() => {
    if (callState.status === CallStatus.CONNECTING) {
      startCall();
      // Start connection timeout
      connectionTimeoutRef.current = setTimeout(() => {
        setCallState((prev) => ({
          ...prev,
          status: CallStatus.ERROR,
          error:
            "Connection timed out. Please check your internet connection and try again.",
        }));
      }, CONNECTION_TIMEOUT);
    }

    return () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
    };
  }, [callState.status]);

  useEffect(() => {
    const handleCallStart = () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
      setCallState((prev) => ({ ...prev, status: CallStatus.CONNECTED }));
      if (onCallStarted) onCallStarted();
      Sentry.addBreadcrumb({
        category: "call",
        message: "Call connected",
        level: "info",
      });
    };

    const handleError = (error: ExplicitAny) => {
      Sentry.captureException(error);
      if (error.action === "camera-error") {
        endingIntentRef.current = "error";
        vapi.stop();
        setCallState((prev) => ({
          ...prev,
          status: CallStatus.ERROR,
          error:
            "We can't access your microphone. Please check your browser settings and try again.",
        }));
      }
    };

    vapi.on("call-start", handleCallStart);
    vapi.on("call-end", handleCallEnded);
    vapi.on("error", handleError);
    if (onVolumeChange) vapi.on("volume-level", onVolumeChange);

    return () => {
      vapi.removeListener("call-start", handleCallStart);
      vapi.removeListener("call-end", handleCallEnded);
      vapi.removeListener("error", handleError);
      if (onVolumeChange) vapi.removeListener("volume-level", onVolumeChange);
    };
  }, [isConnected]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (callState.status === CallStatus.CONNECTED) {
      interval = setInterval(() => setCallDuration((prev) => prev + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [callState.status]);

  useEffect(() => {
    if (saveError && retrySavingCount < MAX_RETRY_SAVING_COUNT) {
      handleRetrySave();
    }

    if (saveError && retrySavingCount >= MAX_RETRY_SAVING_COUNT) {
      Sentry.captureException(saveError);
      setCallState((prev) => ({
        ...prev,
        status: CallStatus.SAVE_FAILED,
        error: "Failed to save the call. Please try again.",
      }));

      setRetrySavingCount(0);
    }

    if (!saveError) {
      setRetrySavingCount(0);
    }
  }, [saveError]);

  function handleStartCall() {
    enable(); // Prevent screen from sleeping
    setCallState((prev) => ({
      ...prev,
      status: skipCountdown ? CallStatus.CONNECTING : CallStatus.STARTING,
      error: null,
    }));
  }

  function handleCancelCall() {
    disable(); // Allow screen to sleep
    endingIntentRef.current = "cancel";
    vapi.stop();
    setCallDuration(0);
    callIdRef.current = null;
    setCallState((prev) => ({ ...prev, status: CallStatus.IDLE, error: null }));
    Sentry.addBreadcrumb({
      category: "call",
      message: "Call cancelled",
      level: "info",
    });
  }

  function handleCallEnded() {
    disable(); // Allow screen to sleep

    // Check the intent - default to 'save' if null (Vapi-initiated end)
    const intent = endingIntentRef.current || "save";

    // Only save if the intent is to save (user ended or Vapi ended)
    if (intent === "save") {
      setCallState((prev) => ({ ...prev, status: CallStatus.ENDING }));
      if (callIdRef.current) {
        handleSaveCall(callIdRef.current);
      }
    }
    // Reset the intent
    endingIntentRef.current = null;

    Sentry.addBreadcrumb({
      category: "call",
      message: `Call ended with intent: ${intent}`,
      level: "info",
    });
  }

  function handleUserEndCall() {
    endingIntentRef.current = "save";
    vapi.stop();
  }

  function handleRestartCall() {
    endingIntentRef.current = "restart";
    vapi.stop();
    setCallState((prev) => ({
      ...prev,
      status: CallStatus.RESTARTING,
      error: null,
    }));
    setCallDuration(0);
    callIdRef.current = null;
    if (onCallRestarted) onCallRestarted();
    Sentry.addBreadcrumb({
      category: "call",
      message: "Call restarted",
      level: "info",
    });
  }

  function handleRetrySave() {
    setCallState((prev) => ({ ...prev, status: CallStatus.ENDING }));
    if (callIdRef.current) {
      handleSaveCall(callIdRef.current);
    }
    setRetrySavingCount((prev) => prev + 1);
  }

  function sendBackgroundMessage(message: string) {
    if (callIdRef.current) {
      vapi.send({
        type: "add-message",
        message: {
          role: "system",
          content: message,
        },
        // @ts-ignore: `triggerResponseEnabled` is a valid property according to Vapi docs at https://docs.vapi.ai/calls/call-features#2-add-message-to-conversation, but is invalid according to the type definition of the @vapi-ai/web NPM package.
        //
        // This property is added to avoid interrupting the voice AI when the slide changes.
        //
        // Reference: (email reply from Vapi to Brian's request on why the voice AI is getting interrupted when calling `add-message` although their docs says otherwise):
        // Hey Jesse, when you use add-message of live call control the triggerResponseEnabled flag is set to true by default if its set to true then it interrupts the assistant to generate next response. You have set this flag to false when generating the next response. https://docs.vapi.ai/calls/call-features#2-add-message-to-conversation
        //
        // Regards,
        // Shubham Bajaj
        // Solutions Engineer
        triggerResponseEnabled: false,
      });
    }
  }

  // Create props object for child render function
  const renderProps: LiveCallRenderProps = {
    callState,
    callDuration,
    onStartCall: handleStartCall,
    onCancelCall: handleCancelCall,
    onEndCall: handleUserEndCall,
    onRestartCall: handleRestartCall,
    onRetrySave: handleRetrySave,
    onOpenResetConfirm: () => setIsOpenResetConfirmDialog(true),
    introductionAudioUrl,
    disabled,
    sendBackgroundMessage,
  };

  return (
    <>
      {children(renderProps)}
      <RoleplaySessionResetConfirm
        open={isOpenResetConfirmDialog}
        onClose={() => setIsOpenResetConfirmDialog(false)}
        onConfirm={() => {
          handleRestartCall();
          setIsOpenResetConfirmDialog(false);
        }}
      />
    </>
  );
}
